// $FlowFixMe
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Button,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { bindActionCreators } from 'redux';
import { isPristine, isInvalid, submit } from 'redux-form';
import { connect } from 'react-redux';
import { pathOr } from 'ramda';
import messages from './messages';

import { useAsyncSaveUnit } from './hooks';
import GeneralUnitInformationForm from './EditUnitForm';
import { promptToaster } from '../../../App/actions';
import { FORM_NAME } from '../constants';

type ConnectedProps = {
  pristine: boolean,
  invalid: boolean,
};

type Props = ConnectedProps & {
  refreshUnit: Function,
  getUnitById: Function,
  unitToEdit: Object,
  onClose: Function,
  show: boolean,
  reset: Function,
  selectedProperty: Object,
  actions: Object,
  intl: Object,
  flags: Object,
  isPHFloorPlan: boolean,
  isHUDFloorPlan: boolean,
  showXmlUnitNumberField: boolean,
};

const EditUnitGeneralInformationFormModal = ({
  unitToEdit,
  refreshUnit,
  getUnitById,
  pristine,
  invalid,
  onClose,
  show,
  actions,
  intl,
  selectedProperty,
  flags,
  isPHFloorPlan,
  isHUDFloorPlan,
  showXmlUnitNumberField,
}: Props) => {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const hideCancelConfirmation = () => setShowCancelConfirmation(false);

  // Route the X icon, backdrop click, and Cancel button through the pristine check.
  const handleHide = () => {
    if (!pristine && !showCancelConfirmation) {
      setShowCancelConfirmation(true);
      return;
    }
    setShowCancelConfirmation(false);
    if (onClose) {
      onClose();
    }
  };

  const unitId = pathOr('', ['id'], unitToEdit);
  const saveUnitInfo = useAsyncSaveUnit(
    unitId,
    intl,
    selectedProperty,
    actions.promptToaster,
    refreshUnit,
    getUnitById,
  );

  const submitForm = (evt: Object) => {
    if (evt && evt.preventDefault) {
      evt.preventDefault();
    }
    actions.submit(FORM_NAME);
    onClose();
  };

  return (
    <Modal
      open={show}
      onClose={handleHide}
      title={
        <Typography variant="titleLarge">
          <FormattedMessage {...messages.modalHeader} />
        </Typography>
      }
      ModalProps={{
        PaperProps: {
          // width is per-modal sizing; all spacing/radius comes from the shared component.
          sx: { width: 497, maxWidth: 497 },
        },
      }}
      ContentProps={{ dividers: true, sx: { position: 'relative' } }}
      ActionsProps={{ sx: { justifyContent: 'center' } }}
      actions={
        <>
          <Button
            variant="primarySubtle"
            onClick={handleHide}
            sx={{ width: 180 }}
          >
            <FormattedMessage {...messages.modalCancel} />
          </Button>
          <Button
            type="submit"
            variant="primary"
            onClick={submitForm}
            disabled={pristine || invalid}
            sx={{ width: 180 }}
          >
            <FormattedMessage {...messages.modalSubmit} />
          </Button>
        </>
      }
    >
      {showCancelConfirmation && (
        <Stack
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            backgroundColor: '#f7f7f7',
            alignItems: 'center',
            pt: 5,
            px: 3,
          }}
        >
          <Typography variant="titleLarge" sx={{ textAlign: 'center', mb: 3 }}>
            <FormattedMessage {...messages.confirmationTitle} />
          </Typography>
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button variant="primary" onClick={handleHide} sx={{ flex: 1 }}>
              <FormattedMessage {...messages.confirmation} />
            </Button>
            <Button
              variant="primarySubtle"
              onClick={hideCancelConfirmation}
              sx={{ flex: 1 }}
            >
              <FormattedMessage {...messages.confirmationCancel} />
            </Button>
          </Stack>
        </Stack>
      )}
      <GeneralUnitInformationForm
        onSubmit={saveUnitInfo}
        initialValues={unitToEdit}
        intl={intl}
        showXmlUnitNumberField={showXmlUnitNumberField}
        selectedProperty={selectedProperty}
        flags={flags}
        isPHFloorPlan={isPHFloorPlan}
        isHUDFloorPlan={isHUDFloorPlan}
      />
    </Modal>
  );
};

export const mapStateToProps = ({ app, form, unitToEdit }: Object): Object => {
  // Using { form } because Redux helpers expect state object
  const pristine = isPristine(FORM_NAME)({ form });
  const invalid = isInvalid(FORM_NAME)({ form });
  return {
    pristine,
    invalid,
    selectedProperty: app.selectedProperty,
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      promptToaster,
      submit,
    },
    dispatch,
  );
  return { actions };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditUnitGeneralInformationFormModal);
